import { inject, Injectable } from '@angular/core';
import { AuthService } from '@evc/platform';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions } from '@microsoft/signalr';
import { CookieService } from 'ngx-cookie';

import { CanvasCommunicationService, NetworkSettings, NetworkSettingsComponent } from '@olympus/ngx-shared';
import { nullish } from 'shared-utils/data';

import { environment } from '../../../../environments/environment';
import { WindViewPreference } from '../../app.preference';
import { ConfigService } from '../config';
import { OscCloudService } from '../osc-cloud/osc-cloud.service';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  private readonly _authService = environment.USE_CONNECT_PLATFORM ? inject(AuthService) : null;

  public dpuId: string | null = null;

  public connection: HubConnection | undefined;
  private _dpuTimeout: any;

  constructor(
    private readonly _cloud: OscCloudService,
    private readonly _communicationService: CanvasCommunicationService,
    private readonly _config: ConfigService,
    private readonly _cookieService: CookieService,
    private readonly _preference: WindViewPreference,
  ) { }

  public initConnection(): void {
    const url: string = this.getUrl();
    const options: IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      accessTokenFactory: () => this._authService?.accessToken() ?? this._cookieService.get('id_token') ?? '',
    };

    this.connection = new HubConnectionBuilder()
      .withAutomaticReconnect()
      // NOTE Angular proxy will crash if it can't connect to the websocket, until a solution is found
      // we'll bypass the proxy by connecting directly to the websocket address
      .withUrl(url, options)
      .build();

    // Happens when on a reconnection, not the first time
    /* istanbul ignore next */
    this.connection.onreconnected(() => {
      console.log('on reconnected');
      this._cloud.setStatus(true);
    });

    // Happens when the server disconnects
    /* istanbul ignore next */
    this.connection.onreconnecting(() => {
      console.log('on reconnecting');
    });

    // Happen when the client disconnects
    /* istanbul ignore next */
    this.connection.onclose(() => {
      console.log('on close');
      this._cloud.setStatus(false);

      this.connect();
    });

    if (this._config.isCloudConnected && !this._config.isLocal) {
      this.connection.on('NewDpuId', (_dpuId: string) => {
        clearTimeout(this._dpuTimeout);
        this.dpuId = _dpuId;

        this.connectToDpu();
      });
    }

    this.connect();
  }

  public disconnect(): Promise<any> {
    console.log('disconnect');

    return this.connection.stop() ?? Promise.resolve();
  }

  private connect(): void {
    console.log('connect');
    const startedConnection = this.connection.start();
    startedConnection.then(() => {
      this._cloud.setStatus(true);
      if (nullish(this.dpuId)) {
        this.getDpu();
      }
    })
      .catch(() => {
        // Automatic reconnect doesn't work for the first connection
        setTimeout(() => {
          this._cloud.setStatus(false);
          this.connect();
        }, 2500);
      });
  }

  private getUrl(): string {
    if (this._config.isLocal) {
      return '//localhost:5000/status-hub';
    }

    if (!environment.USE_CONNECT_PLATFORM) {
      return '/status-hub';
    }

    return `${environment.PROTECTED_URIS?.UNIS_UT}/status-hub`;
  }

  private getDpu(): void {
    this._dpuTimeout = setTimeout(() => {
      this.connection.invoke('GetNewDpuId');
      this.getDpu();
    }, 2500);
  }

  private getDpuUrl(): string {
    if (!environment.USE_CONNECT_PLATFORM) {
      return `${window.location.host}/dpu`;
    }

    // TODO: IVIEW-9587 - Review URI parsing to be more robust and inform developers when the environment is not set as the expected value.
    //       Assuming here that it is always defined and starts with "https://" ?
    return `${environment.PROTECTED_URIS?.UNIS_UT.slice(8)}/dpu`;
  }

  private connectToDpu(): void {
    const settings = this._preference.instant(NetworkSettingsComponent.PREFERENCE_KEY_DPU) as NetworkSettings;

    if (!settings || !this.dpuId) {
      return;
    }

    this._communicationService.communication.disconnect();
    this._communicationService.communication.connect({
      ipAddress: this.getDpuUrl(),
      dpuId: this.dpuId,
      commandPort: settings.portCommand,
      dataPort: settings.portData,
      eventPort: settings.portEvent,
      autoConnect: true,
      secure: true,
      useRoute: false,
      usePingPong: true,
    });

    setInterval(() => {
      if (this.connection.state === HubConnectionState.Connected) {
        this.connection.invoke('KeepAlive', this.dpuId);
      }
    }, 5000);
  }
}
